import { RaasPortalId } from "../../types";
import { type TableProps } from "@findemdev/maui";
import React, { lazy, Suspense, useCallback, useContext, useState } from "react";
import UserContext from "../../../../contexts/UserContext";
import type { IGetCompanyLogosResponseDataObj } from "../../../../types/pub";
import { ITEMS_PER_PAGE } from "../../constants";
import { getRaasProjectCollaboration } from "../../collaboration";
import { RaasProjectCollaborationProvider } from "../../RaasProjectCollaborationContext";
import { StyledTable } from "../../RaasClientPortal.styled";
import type { ISortConfig, IProjectRow, ProjectTableColumnKey, SortableColumnKey, SortOrder } from "../../types";
import { getProjectTableScrollWidth } from "../../utils";
import { useProjectTableColumns } from "./hooks/useProjectTableColumns";

const ShareProjectModal = lazy(() => import("../ShareProjectModal/ShareProjectModal"));

type ProjectTableOnRow = NonNullable<TableProps<IProjectRow>["onRow"]>;
type ProjectTableProps = TableProps<IProjectRow> & { $fillRows?: boolean };

const MauiProjectsTable = StyledTable as React.ComponentType<ProjectTableProps>;
const getProjectRowKey = ({ row }: { row: IProjectRow }) => row.project.ref;

interface IProjectsTableProps {
  portalId?: RaasPortalId;
  columnKeys: readonly ProjectTableColumnKey[];
  companyDetailsByMetaRef: Record<
    string,
    IGetCompanyLogosResponseDataObj | undefined
  >;
  onDeleteProject: ({ project }: { project: IProjectRow["project"] }) => void;
  onEditProject: ({ project }: { project: IProjectRow["project"] }) => void;
  onRowClick: ({ project }: { project: IProjectRow["project"] }) => void;
  onRowKeyDown: (
    args: {
      event: React.KeyboardEvent<HTMLElement>;
      project: IProjectRow["project"];
    },
  ) => void;
  isRowClickable: (row: IProjectRow) => boolean;
  onSort: (field: SortableColumnKey, order: SortOrder | null) => void;
  rows: IProjectRow[];
  sortConfig: ISortConfig | null;
}

export const ProjectsTable = ({
  columnKeys,
  portalId,
  companyDetailsByMetaRef,
  isRowClickable,
  onDeleteProject,
  onEditProject,
  onRowClick,
  onRowKeyDown,
  onSort,
  rows,
  sortConfig,
}: IProjectsTableProps) => {
  const currentUser = useContext(UserContext).getUser?.();
  const [sharingProjectRef, setSharingProjectRef] = useState<string>();
  const sharingProject = rows.find(row => row.project.ref === sharingProjectRef)?.project;
  const handleShareProject = useCallback(({ project }: { project: IProjectRow["project"] }) => {
    setSharingProjectRef(project.ref);
  }, []);
  const tableColumns = useProjectTableColumns({
    columnKeys,
    portalId,
    companyDetailsByMetaRef,
    onDeleteProject,
    onEditProject,
    onShareProject: handleShareProject,
    onSort,
    sortConfig,
  });

  const tableOnRow = useCallback<ProjectTableOnRow>(
    (row) => {
      const clickable = isRowClickable(row);
      return {
        "data-project-ref": row.project.ref,
        onClick: clickable ? () => onRowClick({ project: row.project }) : undefined,
        onKeyDown: clickable ? (event) => onRowKeyDown({ event, project: row.project }) : undefined,
        role: clickable ? "button" : undefined,
        tabIndex: clickable ? 0 : undefined,
        style: clickable ? undefined : { cursor: "default" },
      };
    },
    [isRowClickable, onRowClick, onRowKeyDown],
  );

  return (
    <>
      <MauiProjectsTable
        $fillRows={rows.length === ITEMS_PER_PAGE}
        columns={tableColumns}
        dataSource={rows}
        onRow={tableOnRow}
        pagination={false}
        rowKey={(row) => getProjectRowKey({ row })}
        scroll={{
          x: getProjectTableScrollWidth({ columnKeys }),
        }}
        tableLayout="fixed"
      />
      {portalId === RaasPortalId.Client && sharingProject && currentUser ? (
        <RaasProjectCollaborationProvider value={getRaasProjectCollaboration({ currentUser, project: sharingProject })}>
          <Suspense fallback={null}>
            <ShareProjectModal
              key={sharingProject.ref}
              project={sharingProject}
              currentUser={currentUser}
              onClose={() => setSharingProjectRef(undefined)}
            />
          </Suspense>
        </RaasProjectCollaborationProvider>
      ) : null}
    </>
  );
};
